import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { driverApi, escortApi, assignmentApi } from '../api/mockApi';
import Avatar from '../components/Avatar';

const VEHICLE_TYPES = ['Saloon Car', 'Wheelchair Minibus', 'Black Cab', '7 Seater MPV', '9 Seater MPV', 'W/C Taxi'];
const QUALIFICATIONS = ['DBS', 'First Aid', 'Safeguard', 'PATS'];
const LICENSE_OPTIONS = ['PCO', 'LOCAL AUTHORITY', 'TAXI BADGE', 'PSV'];
const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];

const emptyPa = () => ({
  fullName: '', mobileNumber: '', address: '', postCode: '', area: '',
  qualifications: [], licenses: '', attachments: [], routeStatus: 'OFF ROUTE',
});

// Build the inline PA form values: prefer the PA the server embedded on the
// driver, otherwise fall back to the escort currently assigned to this driver
// so editing updates that same record instead of creating a duplicate.
const paForForm = (driver, escortList) => {
  if (!driver) return emptyPa();
  const source = driver.pa || (escortList || []).find(e => e.id === driver.assignedEscort);
  if (!source) return emptyPa();
  return {
    fullName: source.fullName || '',
    mobileNumber: source.mobileNumber || '',
    address: source.address || '',
    postCode: source.postCode || '',
    area: source.area || '',
    qualifications: source.qualifications || [],
    licenses: source.licenses || '',
    attachments: source.attachments || [],
    routeStatus: source.routeStatus || 'OFF ROUTE',
  };
};

const formFromDriver = (driver, escortList) => ({
  ...driver,
  area: driver.area || '',
  pa: paForForm(driver, escortList),
});

const formatCoord = (value) => {
  const num = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(num)) return 'N/A';
  return num.toFixed(5);
};

const DriverDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [escorts, setEscorts] = useState([]);
  const [showAssign, setShowAssign] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [driverRes, escortsRes] = await Promise.all([
        driverApi.getById(id),
        escortApi.getAll(),
      ]);
      const escortList = escortsRes && escortsRes.success && Array.isArray(escortsRes.data) ? escortsRes.data : [];
      setEscorts(escortList);
      if (driverRes && driverRes.success) {
        setDriver(driverRes.data);
        setForm(formFromDriver(driverRes.data, escortList));
      }
    };
    load();
  }, [id]);

  const startEdit = () => {
    setFormError('');
    setForm(formFromDriver(driver, escorts));
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    // Never round-trip the stored coordinates, and drop the auto-derived area
    // once the post code moves — the server only re-geocodes when the client
    // leaves latitude/longitude/area unset.
    const { pa, routeName, latitude, longitude, ...rest } = form;
    const postCodeChanged = String(form.postCode || '') !== String(driver.postCode || '');
    const areaEdited = String(form.area || '') !== String(driver.area || '');
    const payload = {
      ...rest,
      area: postCodeChanged && !areaEdited ? '' : (form.area || ''),
    };

    if (form.paAvailability === 'Available') {
      const details = pa || emptyPa();
      const paName = String(details.fullName || '').trim();
      const paMobile = String(details.mobileNumber || '').trim();
      if (!paName || !paMobile) {
        setFormError('PA Full Name and PA Mobile Number are required when PA Availability is "Available".');
        return;
      }
      const basePa = paForForm(driver, escorts);
      const paPostCodeChanged = String(details.postCode || '') !== String(basePa.postCode || '');
      const paAreaEdited = String(details.area || '') !== String(basePa.area || '');
      payload.pa = {
        ...details,
        fullName: paName,
        mobileNumber: paMobile,
        area: paPostCodeChanged && !paAreaEdited ? '' : (details.area || ''),
      };
    }

    setSaving(true);
    const res = await driverApi.update(id, payload);
    setSaving(false);
    if (!res || !res.success) {
      setFormError((res && res.error) || 'Could not save this driver. Please try again.');
      return;
    }
    setDriver(res.data);
    setForm(formFromDriver(res.data, escorts));
    setEditing(false);
  };

  const handleStatusToggle = async () => {
    const newStatus = driver.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE';
    const res = await driverApi.updateRouteStatus(id, newStatus);
    if (res && res.success) setDriver(prev => ({ ...prev, ...res.data }));
  };

  const handleAssignPA = async (escortId) => {
    await assignmentApi.assignEscortToDriver(id, escortId);
    const [driverRes, escortsRes] = await Promise.all([
      driverApi.getById(id),
      escortApi.getAll(),
    ]);
    const escortList = escortsRes && escortsRes.success && Array.isArray(escortsRes.data) ? escortsRes.data : escorts;
    setEscorts(escortList);
    if (driverRes && driverRes.success) {
      setDriver(driverRes.data);
      setForm(formFromDriver(driverRes.data, escortList));
    }
    setShowAssign(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Delete this driver?')) {
      await driverApi.delete(id);
      navigate('/drivers');
    }
  };

  const toggleQual = (q) => {
    setForm(prev => ({
      ...prev,
      qualifications: prev.qualifications?.includes(q)
        ? prev.qualifications.filter(x => x !== q)
        : [...(prev.qualifications || []), q],
    }));
  };

  const updatePa = (patch) => {
    setForm(prev => ({ ...prev, pa: { ...(prev.pa || emptyPa()), ...patch } }));
  };

  const togglePaQual = (q) => {
    setForm(prev => {
      const pa = prev.pa || emptyPa();
      const quals = pa.qualifications || [];
      return {
        ...prev,
        pa: {
          ...pa,
          qualifications: quals.includes(q) ? quals.filter(x => x !== q) : [...quals, q],
        },
      };
    });
  };

  const handleDownload = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'document';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!driver) return <div className="loading">Loading...</div>;

  const assignedPA = driver.pa || escorts.find(e => e.id === driver.assignedEscort) || null;
  const ownPA = !!(assignedPA && assignedPA.belongsToDriver && assignedPA.belongsToDriver === driver.id);
  const formPa = form.pa || emptyPa();

  return (
    <div>
      <Link to="/drivers" className="back-link">← Back to Drivers</Link>

      <div className="page-header">
        <div className="title-with-avatar">
          <Avatar name={driver.fullName} photoUrl={driver.photoUrl} size={64} />
          <div>
            <h1 className="page-title" style={{ marginBottom: 2 }}>{driver.fullName || 'Driver Details'}</h1>
            <span className={`distance-badge ${driver.photoUrl ? 'within' : 'outside'}`}>
              {driver.photoUrl ? 'Verification selfie on file' : 'Awaiting verification selfie'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-outline btn-sm" onClick={handleStatusToggle}>
            Switch to {driver.routeStatus === 'ON ROUTE' ? 'OFF ROUTE' : 'ON ROUTE'}
          </button>
          <button className="btn btn-gold btn-sm" onClick={startEdit}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      {!editing ? (
        <>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>Personal Details</h3>
              <div className="detail-row"><span className="detail-label">Full Name</span><span className="detail-value">{driver.fullName || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Address</span><span className="detail-value">{driver.address || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Post Code</span><span className="detail-value">{driver.postCode || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{driver.area || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Mobile</span><span className="detail-value">{driver.mobileNumber}</span></div>
            </div>
            <div className="detail-card">
              <h3>Vehicle & License</h3>
              <div className="detail-row"><span className="detail-label">Vehicle</span><span className="detail-value">{driver.vehicle || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">License</span><span className="detail-value">{driver.licenses || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">PA Availability</span><span className="detail-value">{driver.paAvailability || 'N/A'}</span></div>
            </div>
            <div className="detail-card">
              <h3>Route Status</h3>
              <span className={`status-badge ${driver.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`} style={{ fontSize: 16, padding: '8px 20px' }}>
                <span className="dot"></span>
                {driver.routeStatus}
              </span>
              <div className="detail-row" style={{ marginTop: 14 }}><span className="detail-label">Route</span><span className="detail-value">{driver.routeName || 'Not assigned'}</span></div>
            </div>
            <div className="detail-card">
              <h3>Location</h3>
              <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{driver.area || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Latitude</span><span className="detail-value">{formatCoord(driver.latitude)}</span></div>
              <div className="detail-row"><span className="detail-label">Longitude</span><span className="detail-value">{formatCoord(driver.longitude)}</span></div>
            </div>
            <div className="detail-card">
              <h3>Qualifications</h3>
              <div className="qual-badges">
                {QUALIFICATIONS.map(q => (
                  <span key={q} className={`qual-badge ${driver.qualifications?.includes(q) ? 'active' : 'inactive'}`}>{q}</span>
                ))}
              </div>
            </div>
            <div className="detail-card">
              <h3>Assigned Passenger Assistant</h3>
              {assignedPA ? (
                <div>
                  {ownPA && (
                    <div style={{ marginBottom: 10 }}>
                      <span className="pill">Added with this driver</span>
                    </div>
                  )}
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{assignedPA.fullName || 'N/A'}</span></div>
                  <div className="detail-row"><span className="detail-label">Mobile</span><span className="detail-value">{assignedPA.mobileNumber || 'N/A'}</span></div>
                  <div className="detail-row"><span className="detail-label">Post Code</span><span className="detail-value">{assignedPA.postCode || 'N/A'}</span></div>
                  <div className="detail-row"><span className="detail-label">Area</span><span className="detail-value">{assignedPA.area || 'N/A'}</span></div>
                  <div className="detail-row"><span className="detail-label">Status</span>
                    <span className={`status-badge ${assignedPA.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                      <span className="dot"></span>
                      {assignedPA.routeStatus || 'OFF ROUTE'}
                    </span>
                  </div>
                  <div className="qual-badges" style={{ marginTop: 12 }}>
                    {QUALIFICATIONS.map(q => (
                      <span key={q} className={`qual-badge ${assignedPA.qualifications?.includes(q) ? 'active' : 'inactive'}`}>{q}</span>
                    ))}
                  </div>
                  {assignedPA.id && (
                    <div style={{ marginTop: 12 }}>
                      <Link to={`/escorts/${assignedPA.id}`} className="back-link">View PA details →</Link>
                    </div>
                  )}
                </div>
              ) : (
                <p className="empty-state">No PA assigned</p>
              )}
              <button className="btn btn-outline btn-sm" style={{ marginTop: 12 }} onClick={() => setShowAssign(true)}>
                {assignedPA ? 'Change PA' : 'Assign PA'}
              </button>
            </div>
            <div className="detail-card">
              <h3>Documents</h3>
              {driver.attachments?.length ? (
                <div>
                  {driver.attachments.map((att, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(174,138,73,0.1)' }}>
                      <span style={{ color: '#CAB069', fontSize: 14 }}>{att.originalName || att.filename || `Document ${i+1}`}</span>
                      <button className="btn btn-outline btn-sm" onClick={() => handleDownload(att.url, att.originalName)}>Download</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#818181', fontStyle: 'italic' }}>No documents uploaded</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="modal" style={{ maxWidth: 600, margin: '0 auto' }}>
          <h3>Edit Driver</h3>
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Full Name</label>
              <input value={form.fullName || ''} onChange={e => setForm({...form, fullName: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Mobile Number</label>
              <input value={form.mobileNumber || ''} onChange={e => setForm({...form, mobileNumber: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input value={form.address || ''} onChange={e => setForm({...form, address: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Post Code</label>
              <input value={form.postCode || ''} onChange={e => setForm({...form, postCode: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Area</label>
              <input
                value={form.area || ''}
                placeholder="Leave blank to detect from post code"
                onChange={e => setForm({...form, area: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Vehicle</label>
              <select value={form.vehicle || ''} onChange={e => setForm({...form, vehicle: e.target.value})}>
                <option value="">Select</option>
                {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Qualifications</label>
              <div className="checkbox-group">
                {QUALIFICATIONS.map(q => (
                  <div key={q} className={`checkbox-item ${form.qualifications?.includes(q) ? 'checked' : ''}`} onClick={() => toggleQual(q)}>
                    {form.qualifications?.includes(q) ? '✓' : '○'} {q}
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>License</label>
              <select value={form.licenses || ''} onChange={e => setForm({...form, licenses: e.target.value})}>
                <option value="">Select license</option>
                {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>PA Availability</label>
              <select value={form.paAvailability || ''} onChange={e => setForm({...form, paAvailability: e.target.value})}>
                <option value="Available">Available</option>
                <option value="Not Available">Not Available</option>
              </select>
            </div>

            {form.paAvailability === 'Available' && (
              <div className="form-section">
                <h4>Passenger Assistant Details</h4>
                <p className="hint">This PA belongs to this driver and will be linked to them automatically.</p>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input value={formPa.fullName} onChange={e => updatePa({ fullName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input value={formPa.mobileNumber} onChange={e => updatePa({ mobileNumber: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input value={formPa.address} onChange={e => updatePa({ address: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Post Code</label>
                  <input value={formPa.postCode} onChange={e => updatePa({ postCode: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Area</label>
                  <input
                    value={formPa.area}
                    placeholder="Leave blank to detect from post code"
                    onChange={e => updatePa({ area: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Qualifications</label>
                  <div className="checkbox-group">
                    {QUALIFICATIONS.map(q => (
                      <div key={q} className={`checkbox-item ${formPa.qualifications?.includes(q) ? 'checked' : ''}`} onClick={() => togglePaQual(q)}>
                        {formPa.qualifications?.includes(q) ? '✓' : '○'} {q}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>License</label>
                  <select value={formPa.licenses} onChange={e => updatePa({ licenses: e.target.value })}>
                    <option value="">Select license</option>
                    {LICENSE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Route Status</label>
                  <select value={formPa.routeStatus} onChange={e => updatePa({ routeStatus: e.target.value })}>
                    {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}

            {formError && <p className="error-text" style={{ textAlign: 'left' }}>{formError}</p>}

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => { setEditing(false); setFormError(''); }}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {showAssign && (
        <div className="modal-overlay" onClick={() => setShowAssign(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Assign Passenger Assistant</h3>
            <p style={{ color: '#818181', marginBottom: 16 }}>Select a PA to assign:</p>
            {escorts.filter(e => !e.assignedDriver || e.id === driver.assignedEscort).map(e => (
              <div key={e.id} style={{
                padding: '12px 16px', borderRadius: 10, border: '1px solid #AE8A49',
                marginBottom: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                backgroundColor: e.id === driver.assignedEscort ? 'rgba(76,175,80,0.2)' : '#2A2A2A',
              }} onClick={() => handleAssignPA(e.id)}>
                <span style={{ color: '#CAB069' }}>{e.fullName}</span>
                <span className={`status-badge ${e.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>{e.routeStatus}
                </span>
              </div>
            ))}
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowAssign(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDetailPage;
